import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaRegEye, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import { Loader, Pagination } from 'components';
import { routes } from 'common';
import { api } from 'api';
import '../bookings/_bookings.scss';

const filterOptions = [
  { label: 'Whole Day', value: 'whole_day' },
  { label: 'Time Specific', value: 'time_specific' },
];

const sortOptions = [
  { value: '-start_date', label: 'Start Date (Newest)' },
  { value: 'start_date', label: 'Start Date (Oldest)' },
  { value: '-created', label: 'Date Added (Newest)' },
  { value: 'created', label: 'Date Added (Oldest)' },
];

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};

// Helper function to format time
const formatTime = (timeString) => {
  if (!timeString) return '';
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function Maintenance() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('-start_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch maintenance
  const fetchMaintenance = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        page_size: itemsPerPage,
        ...(filter !== 'all' && { is_whole_day: filter === 'whole_day' }),
        ...(search && { search }),
        ...(sortBy && { ordering: sortBy }),
      };

      const response = await api.getMaintenance(params);
      setMaintenance(response.results || []);
      setTotalItems(response.count || 0);
    } catch (err) {
      setError(err);
      toast.error('Failed to fetch maintenance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaintenance();
  }, [currentPage, itemsPerPage, filter, search, sortBy]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (error) {
    return (
      <div className="bookings-page custom-listing">
        <div className="error-message">
          <h3>Error loading maintenance</h3>
          <p>{error?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={fetchMaintenance}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-page custom-listing">
      {loading && <Loader />}
      <div className="listing-header">
        <h2 className="listing-title">Maintenance Management</h2>
        <button className="listing-create-btn" onClick={() => navigate(routes.MAINTENANCE_CREATE)}>
          Add Maintenance
        </button>
      </div>
      <div className="listing-filters">
        <div className="filter-group">
          <Filters filters={filterOptions} selectedFilter={filter} handleFilter={setFilter} />
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={setSortBy} />
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search maintenance..."
            debounceDelay={500}
            onDebouncedChange={setSearch}
          />
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Ground</th>
              <th>Field</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Type</th>
              <th>Time Range</th>
              <th>Reason</th>
              <th>Date Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {maintenance.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', color: '#888' }}>
                  No maintenance periods found.
                </td>
              </tr>
            ) : (
              maintenance.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.ground_name || 'All Fields'}</td>
                  <td>{item.ground_field_name || 'N/A'}</td>
                  <td>{formatDate(item.start_date)}</td>
                  <td>{formatDate(item.end_date)}</td>
                  <td>
                    <span className={`badge ${item.is_whole_day ? 'badge-info' : 'badge-warning'}`}>
                      {item.is_whole_day ? 'Whole Day' : 'Time Specific'}
                    </span>
                  </td>
                  <td>
                    {item.is_whole_day
                      ? 'All Day'
                      : item.start_time && item.end_time
                      ? `${formatTime(item.start_time)} - ${formatTime(item.end_time)}`
                      : 'N/A'}
                  </td>
                  <td>{item.reason || 'N/A'}</td>
                  <td>{formatDate(item.created)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        className="view-btn"
                        onClick={() => navigate(`/maintenance/${item.id}`)}
                        title="View details"
                      >
                        <FaRegEye />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
      />
    </div>
  );
}

export default Maintenance;

